import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  HookFormCheckbox,
  HookFormCountrySelect,
  HookFormEmailInput,
  HookFormPasswordInput,
  HookFormTextInput,
} from '@realtyka/real-ui';
import React, { ReactNode, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useRefinement } from '../../hooks/useRefinement';
import { createGenericSignUpSchema } from '../../schemas/GenericSignUpSchema';
import AuthPageLayoutV2 from '../Layout/AuthPageLayoutV2';
import SignUpLeoImg from '../../assets/img/auth/signup-leo-prompt.png';
import { AnalyticsEventEnum } from '../../types';

// Default props to prevent recreation on each render
const DEFAULT_SUBMIT_BUTTON_TEXT = 'Create Account';
const DEFAULT_RIGHT_IMAGE_ALT =
  'Turn Listings Into Deals with first AI-Powered Real Estate Platform';
const DEFAULT_LAYOUT = { columns: 2 as 1 | 2, fullWidthFields: [] as string[] };

interface FieldConfig {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: any;
}

interface GenericSignUpFormProps {
  fields?: {
    firstName?: boolean;
    lastName?: boolean;
    country?: boolean;
    username?: boolean;
    email?: boolean;
    password?: boolean;
    confirmPassword?: boolean;
    termsConsent?: boolean;
    callMessageConsent?: boolean;
  };

  fieldConfig?: {
    firstName?: FieldConfig;
    lastName?: FieldConfig;
    country?: FieldConfig & { countryEnum?: any };
    username?: FieldConfig;
    email?: FieldConfig;
    password?: FieldConfig;
    confirmPassword?: FieldConfig;
    termsConsent?: { label?: ReactNode; defaultValue?: boolean };
    callMessageConsent?: { label?: ReactNode; defaultValue?: boolean };
  };

  layout?: {
    columns?: 1 | 2;
    fullWidthFields?: string[];
  };

  refinements?: {
    usernameRefinement?: (value: string) => Promise<boolean>;
    emailRefinement?: (value: string) => Promise<boolean>;
  };

  onSubmit: (values: any) => void | Promise<void>;

  loading?: boolean;
  submitError?: string;
  submitButtonText?: string;
  submitButtonIcon?: IconDefinition;
  submitButtonDisabled?: boolean;

  secondaryAction?: {
    text: string;
    onClick: () => void;
    icon?: IconDefinition;
  };

  customSchema?: z.ZodSchema;

  title?: string;
  subtitle?: string;

  customErrors?: Record<string, string>;

  defaultValues?: Record<string, any>;

  showRightImage?: boolean;
  rightImageSrc?: string;
  rightImageAlt?: string;
  analyticsEventName?: AnalyticsEventEnum;
}

type FormData = {
  firstName?: string;
  lastName?: string;
  country?: string;
  username?: string;
  emailAddress?: string;
  password?: string;
  confirmPassword?: string;
  consentedToTerms?: boolean;
  consentedToCallMessage?: boolean;
};

const GenericSignUpForm: React.FC<GenericSignUpFormProps> = (props) => {
  const {
    fields = {},
    fieldConfig = {},
    layout = DEFAULT_LAYOUT,
    refinements = {},
    onSubmit,
    loading = false,
    submitError,
    submitButtonText = DEFAULT_SUBMIT_BUTTON_TEXT,
    submitButtonIcon = faChevronRight,
    submitButtonDisabled = false,
    secondaryAction,
    customSchema,
    title,
    subtitle,
    customErrors = {},
    defaultValues = {},
    showRightImage = false,
    rightImageSrc = SignUpLeoImg,
    rightImageAlt = DEFAULT_RIGHT_IMAGE_ALT,
    analyticsEventName,
  } = props;
  // Memoize enabledFields to prevent recreation on every render
  const enabledFields = useMemo(
    () => ({
      firstName: fields.firstName !== false,
      lastName: fields.lastName !== false,
      country: fields.country !== false,
      username: fields.username !== false,
      email: fields.email !== false,
      password: fields.password !== false,
      confirmPassword: fields.confirmPassword !== false,
      termsConsent: fields.termsConsent !== false,
      callMessageConsent: fields.callMessageConsent !== false,
    }),
    [fields],
  );

  const usernameRefinement = useRefinement<string>(
    async (value): Promise<boolean> => {
      if (refinements.usernameRefinement) {
        return await refinements.usernameRefinement(value);
      }
      return true;
    },
    { debounce: 600 },
  );

  const emailRefinement = useRefinement<string>(
    async (value): Promise<boolean> => {
      if (refinements.emailRefinement) {
        return await refinements.emailRefinement(value);
      }
      return true;
    },
    { debounce: 600 },
  );

  // Memoize schema creation to prevent recreation on every render
  const schema = useMemo(
    () =>
      customSchema ||
      createGenericSignUpSchema(enabledFields, {
        usernameRefinement: enabledFields.username
          ? usernameRefinement
          : undefined,
        emailRefinement: enabledFields.email ? emailRefinement : undefined,
      }),
    [customSchema, enabledFields, usernameRefinement, emailRefinement],
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = useForm<FormData>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    Object.entries(defaultValues).forEach(([key, value]) => {
      setValue(key as keyof FormData, value);
    });
  }, [defaultValues, setValue]);

  // Memoize getGridClass function to prevent recreation on every render
  const getGridClass = useCallback(
    (fieldName: string) => {
      const isFullWidth = layout.fullWidthFields?.includes(fieldName);
      if (isFullWidth || layout.columns === 1) {
        return 'col-span-1 md:col-span-2';
      }
      return layout.columns === 2 ? '' : 'col-span-1';
    },
    [layout],
  );

  // Create stable callbacks for onChange handlers
  const handleUsernameChange = useCallback(() => {
    usernameRefinement.invalidate();
  }, [usernameRefinement]);

  const handleEmailChange = useCallback(() => {
    emailRefinement.invalidate();
  }, [emailRefinement]);

  // Memoize className computations
  const gridClassName = useMemo(
    () =>
      `grid grid-cols-1 ${
        layout.columns === 2 ? 'md:grid-cols-2' : ''
      } gap-2 lg:gap-4 pb-6`,
    [layout.columns],
  );

  const displayError = submitError || customErrors.submit;

  // Wrap formContent in useMemo to prevent unnecessary re-creation
  const formContent = useMemo(
    () => (
      <div className='flex flex-col items-center bg-white p-4 md:p-10 gap-6 rounded-xl w-full lg:w-[520px] xl:w-[664px]'>
        {(title || subtitle) && (
          <div className='flex flex-col items-center gap-2'>
            {subtitle && (
              <p className='font-inter-regular font-normal text-sm text-center text-reskin-regent-600'>
                {subtitle}
              </p>
            )}
            {title && (
              <p className='font-poppins-semibold font-semibold text-2xl md:text-[32px] leading-normal tracking-[-1px] text-reskin-primary-dark text-center'>
                {title}
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
          {displayError && (
            <div className='mb-5 flex bg-primary-coral rounded-lg p-2'>
              <p className='font-inter-medium text-white text-base'>
                {displayError}
              </p>
            </div>
          )}

          <div className={gridClassName}>
            {enabledFields.firstName && (
              <div className={getGridClass('firstName')}>
                <HookFormTextInput<FormData, 'firstName'>
                  name='firstName'
                  control={control}
                  label={fieldConfig.firstName?.label || 'First Name'}
                  placeholder={
                    fieldConfig.firstName?.placeholder || 'Enter first name'
                  }
                  disabled={fieldConfig.firstName?.disabled}
                  shouldUnregister={false}
                  withAsterisk
                />
              </div>
            )}

            {enabledFields.lastName && (
              <div className={getGridClass('lastName')}>
                <HookFormTextInput<FormData, 'lastName'>
                  name='lastName'
                  control={control}
                  label={fieldConfig.lastName?.label || 'Last Name'}
                  placeholder={
                    fieldConfig.lastName?.placeholder || 'Enter last name'
                  }
                  disabled={fieldConfig.lastName?.disabled}
                  shouldUnregister={false}
                  withAsterisk
                />
              </div>
            )}

            {enabledFields.country && (
              <div className='col-span-1 md:col-span-2'>
                <HookFormCountrySelect
                  name='country'
                  label={fieldConfig.country?.label || 'Country'}
                  placeholder={
                    fieldConfig.country?.placeholder || 'Select country'
                  }
                  shouldUnregister={false}
                  control={control}
                  countryEnum={fieldConfig.country?.countryEnum}
                  disabled={fieldConfig.country?.disabled}
                  withAsterisk
                />
              </div>
            )}

            {enabledFields.username && (
              <div className={getGridClass('username')}>
                <HookFormTextInput<FormData, 'username'>
                  name='username'
                  control={control}
                  placeholder={
                    fieldConfig.username?.placeholder || 'eg. johndoe9'
                  }
                  label={fieldConfig.username?.label || 'Username'}
                  disabled={fieldConfig.username?.disabled}
                  shouldUnregister={false}
                  onChange={handleUsernameChange}
                  withAsterisk
                />
              </div>
            )}

            {enabledFields.email && (
              <div className={getGridClass('email')}>
                <HookFormEmailInput<FormData, 'emailAddress'>
                  name='emailAddress'
                  control={control}
                  placeholder={
                    fieldConfig.email?.placeholder || 'johndoe@example.com'
                  }
                  label={fieldConfig.email?.label || 'Email'}
                  shouldUnregister={false}
                  disabled={fieldConfig.email?.disabled}
                  onChange={handleEmailChange}
                  withAsterisk
                />
              </div>
            )}

            {enabledFields.password && (
              <div className={getGridClass('password')}>
                <HookFormPasswordInput<FormData>
                  placeholder={
                    fieldConfig.password?.placeholder || 'Choose password'
                  }
                  name='password'
                  label={fieldConfig.password?.label || 'Password'}
                  control={control}
                  shouldUnregister={false}
                  withAsterisk
                />
              </div>
            )}

            {enabledFields.confirmPassword && (
              <div className={getGridClass('confirmPassword')}>
                <HookFormPasswordInput<FormData>
                  placeholder={
                    fieldConfig.confirmPassword?.placeholder ||
                    'Confirm password'
                  }
                  name='confirmPassword'
                  label={
                    fieldConfig.confirmPassword?.label ||
                    'Password Confirmation'
                  }
                  control={control}
                  shouldUnregister={false}
                  withAsterisk
                />
              </div>
            )}
          </div>

          {(enabledFields.termsConsent || enabledFields.callMessageConsent) && (
            <div className='pb-6 space-y-3'>
              {enabledFields.termsConsent && (
                <HookFormCheckbox<FormData, 'consentedToTerms'>
                  name='consentedToTerms'
                  control={control}
                  size='sm'
                  data-testid='consentedToTerms'
                  label={
                    fieldConfig.termsConsent?.label || 'I agree to the terms'
                  }
                />
              )}

              {enabledFields.callMessageConsent && (
                <HookFormCheckbox<FormData, 'consentedToCallMessage'>
                  name='consentedToCallMessage'
                  control={control}
                  size='sm'
                  data-testid='consentedToCallMessage'
                  label={
                    fieldConfig.callMessageConsent?.label ||
                    'I consent to receive calls and messages'
                  }
                />
              )}
            </div>
          )}

          <div className='flex flex-col gap-y-2 w-full'>
            <Button
              type='submit'
              fullWidth
              loading={isSubmitting || loading}
              disabled={submitButtonDisabled}
              rightIcon={submitButtonIcon}
            >
              <span className='font-inter text-sm sm:text-base font-medium truncate'>
                {submitButtonText}
              </span>
            </Button>

            {secondaryAction && (
              <Button
                type='button'
                variant='outline'
                color='secondary'
                fullWidth
                onClick={secondaryAction.onClick}
                rightIcon={secondaryAction.icon}
              >
                <span className='font-inter text-sm sm:text-base font-medium truncate'>
                  {secondaryAction.text}
                </span>
              </Button>
            )}
          </div>
        </form>
      </div>
    ),
    [
      title,
      subtitle,
      displayError,
      handleSubmit,
      onSubmit,
      gridClassName,
      enabledFields,
      fieldConfig,
      control,
      getGridClass,
      handleUsernameChange,
      handleEmailChange,
      isSubmitting,
      loading,
      submitButtonDisabled,
      submitButtonIcon,
      submitButtonText,
      secondaryAction,
    ],
  );

  if (showRightImage) {
    return (
      <AuthPageLayoutV2
        eventName={analyticsEventName}
        rightSection={
          <div className='flex flex-grow justify-center items-start w-full h-full bg-reskin-primary-navy mx-auto'>
            <img
              className='w-full h-screen object-contain'
              src={rightImageSrc}
              alt={rightImageAlt}
            />
          </div>
        }
      >
        <div className='flex flex-col flex-grow justify-center items-center m-6 lg:mx-auto'>
          {formContent}
        </div>
      </AuthPageLayoutV2>
    );
  }

  return formContent;
};

export default GenericSignUpForm;
